import { Component, input, output, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatTableModule, MatTable } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ToolContactForm, createContactGroup, applyContactDetailValidators } from '../../utilities/helpers/tool-form.forms';
import { CONTACT_TYPE_OPTIONS, ContactType, CMDB_CONTACT_TYPES } from 'src/app/features/tooling/models/tool-form-types';
import { CONTACT_TYPE_ID_MAP, CONTACT_TYPE_LABEL } from 'src/app/shared/utilities/mappers/contact-type.mappers';
import { ensureAtLeastOne } from '../../utilities/helpers/form-array-helper';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { GraphSearchService } from '../../services/graph-search.service';
import { GroupResponse } from '../../models/graph/group-response.model';
import { UserResponse } from '../../models/graph/user-response.model';

type ContactNameSuggestion = {
  value: string;
  label: string;
  email: string;
};

@Component({
  selector: 'contacts-table',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './contacts-table.component.html',
  styleUrls: ['./contacts-table.component.scss']
})

export class ContactsTableComponent {
  private readonly fb = inject(FormBuilder);
  private readonly graphSearchService = inject(GraphSearchService);

  contacts = input.required<FormArray<FormGroup<ToolContactForm>>>();
  isLoading = input.required<boolean>();
  
  typeChange = output<number>();

  readonly table = viewChild<MatTable<unknown>>('contactTable');

  readonly displayedColumns = ['type', 'name', 'detail', 'actions'];
  readonly contactTypeOptions = CONTACT_TYPE_OPTIONS;
  readonly cmdbContactTypes = CMDB_CONTACT_TYPES;
  readonly cmdbContactTypeIds = CMDB_CONTACT_TYPES.map(t => CONTACT_TYPE_ID_MAP[t]);

  isCmdbTypeValue(val: unknown): boolean {
    if (typeof val === 'number') return this.cmdbContactTypeIds.includes(val);
    return this.cmdbContactTypes.includes(val as ContactType);
  }

  private readonly searchCache = new WeakMap<FormGroup<ToolContactForm>, Observable<{ results: ContactNameSuggestion[]; loading: boolean }>>();
  private readonly contactNameOptionsByGroup = new WeakMap<FormGroup<ToolContactForm>, ContactNameSuggestion[]>();

  getTypeDisplayLabel(type: ContactType): string {
    let label: string;
    if (typeof type === 'number') label = CONTACT_TYPE_LABEL[type] ?? String(type);
    else label = CONTACT_TYPE_LABEL[type as any] ?? String(type);

    // Hide the ServiceNow prefix in UI labels when present
    return label.replace(/^ServiceNow\s+/i, '').trim();
  }

  isRowReadOnly(group: FormGroup<ToolContactForm>): boolean {
    return Boolean(group?.controls?.isFromCmdb?.value) || this.isCmdbTypeValue(group.controls.type.value);
  }

  contactNameSearch$(group: FormGroup<ToolContactForm>): Observable<{ results: ContactNameSuggestion[]; loading: boolean }> {
    let cached = this.searchCache.get(group);
    if (cached) return cached;

    const name$ = group.controls.name.valueChanges.pipe(startWith(group.controls.name.value ?? ''));
    const type$ = group.controls.type.valueChanges.pipe(startWith(group.controls.type.value));

    cached = combineLatest([name$, type$]).pipe(
      map(([name, type]) => ({ term: String(name ?? '').trim(), type })),
      debounceTime(400),
      distinctUntilChanged((a, b) => a.term === b.term && a.type === b.type),
      switchMap(({ term, type }) => {
        if (!term) return of({ results: [] as ContactNameSuggestion[], loading: false });
        
        if (type == ContactType.SupportLink) return of({ results: [] as ContactNameSuggestion[], loading: false });

        const req$ = type === ContactType.Owner ? this.searchUsers(term) : this.searchGroups(term);
        return req$.pipe(
          map(r => ({ results: r, loading: false })),
          startWith({ results: [] as ContactNameSuggestion[], loading: true }),
          catchError(() => of({ results: [] as ContactNameSuggestion[], loading: false })),
        );
      }),
      tap(payload => this.contactNameOptionsByGroup.set(group, payload.results)),
      shareReplay(1),
    );

    this.searchCache.set(group, cached);
    return cached;
  }

  onContactNameSelected(group: FormGroup<ToolContactForm>, selectedValue: unknown): void {
    if (this.isRowReadOnly(group)) return;
    const value = String(selectedValue ?? '').trim();
    if (!value) return;

    group.controls.name.setValue(value, { emitEvent: false });

    const options = this.contactNameOptionsByGroup.get(group) ?? [];
    const match = options.find(o => o.value === value);
    const email = String(match?.email ?? '').trim();

    if (email) {
      group.controls.contactDetail.setValue(email, { emitEvent: false });
      group.controls.contactDetail.markAsPristine();
      group.controls.contactDetail.markAsUntouched();
    }
  }

  addContact(): void {
    const group = createContactGroup(this.fb);
    this.contacts().push(group);
    this.renderTable();
  }

  removeContact(index: number): void {
    const group = this.contacts().at(index);

    if (this.isRowReadOnly(group)) return;

    this.contacts().removeAt(index);
    ensureAtLeastOne(this.contacts(), () => createContactGroup(this.fb));
    this.renderTable();
  }

  onContactTypeChange(index: number): void {
    const group = this.contacts().at(index);

    if (this.isRowReadOnly(group)) return;
    applyContactDetailValidators(group);
    const detail = group.controls.contactDetail;
    const name = group.controls.name;

    name.setValue('', { emitEvent: false });
    name.markAsPristine();
    name.markAsUntouched();
    detail.setValue('');
    detail.markAsPristine();
    detail.markAsUntouched();

    this.typeChange.emit(index);
  }

  private searchUsers(term: string): Observable<ContactNameSuggestion[]> {
    return this.graphSearchService.searchUsers(term).pipe(
      map((users) => this.toContactNameSuggestions(users)),
      catchError(() => of([])),
    );
  }

  private searchGroups(term: string): Observable<ContactNameSuggestion[]> {
    return this.graphSearchService.searchGroups(term).pipe(
      map((groups) => this.toContactNameSuggestions(groups)),
      catchError(() => of([])),
    );
  }

  private toContactNameSuggestions(items: Array<UserResponse | GroupResponse>): ContactNameSuggestion[] {
    return (items ?? [])
      .map((x) => ({
        value: String(x?.displayName ?? '').trim(),
        label: String(x?.displayName ?? '').trim(),
        email: String(x?.email ?? '').trim(),
      }))
      .filter((x) => Boolean(x.value));
  }

  // ========== CMDB Contact Management ==========

  /**
   * Upsert a contact from CMDB data (Business Owner, Technical Owner, Support Group).
   * These contacts are locked and uneditable.
   */
  upsertCmdbContact(type: ContactType, name: string, email: string): void {
    const normalizedEmail = String(email ?? '').trim().toLowerCase();
    const normalizedName = String(name ?? '').trim();
    if (!normalizedEmail && !normalizedName) return;

    const contactsArray = this.contacts();

    // Check for existing contact with same type and email/name
    const existingSameType = contactsArray.controls.find((g) => {
      if (g.controls.type.value !== type) return false;
      const gEmail = String(g.controls.contactDetail.value ?? '').trim().toLowerCase();
      const gName = String(g.controls.name.value ?? '').trim();
      if (normalizedEmail) return gEmail === normalizedEmail;
      return gName.toLowerCase() === normalizedName.toLowerCase();
    });

    // Check for existing contact with same email but different editable type
    const existingCrossTypeByEmail = normalizedEmail
      ? (contactsArray.controls.find((g) => {
          const currentType = g.controls.type.value;
          if (currentType !== ContactType.Owner && currentType !== ContactType.SupportGroup) return false;
          if (currentType === type) return false;
          if (g.controls.isFromCmdb.value) return false;
          const gEmail = String(g.controls.contactDetail.value ?? '').trim().toLowerCase();
          return gEmail === normalizedEmail;
        }) ?? null)
      : null;

    const target = existingSameType ?? existingCrossTypeByEmail ?? this.findFirstEmptyContactGroup();
    if (target) {
      if (target.controls.type.value !== type) {
        target.controls.type.setValue(type, { emitEvent: false });
        applyContactDetailValidators(target);
      }
      if (normalizedName) target.controls.name.setValue(normalizedName, { emitEvent: false });
      if (normalizedEmail) target.controls.contactDetail.setValue(normalizedEmail, { emitEvent: false });

      this.lockContactGroupFromCmdb(target);
      this.renderTable();
      return;
    }

    // Create new contact group
    const group = createContactGroup(this.fb);
    group.controls.type.setValue(type, { emitEvent: false });
    applyContactDetailValidators(group);
    group.controls.name.setValue(normalizedName, { emitEvent: false });
    group.controls.contactDetail.setValue(normalizedEmail, { emitEvent: false });

    this.lockContactGroupFromCmdb(group);
    contactsArray.push(group);
    this.renderTable();
  }

  /**
   * Remove all CMDB-sourced contacts (when service ID is cleared).
   */
  clearCmdbContacts(): void {
    const contactsArray = this.contacts();
    let removedAny = false;
    
    for (let i = contactsArray.length - 1; i >= 0; i--) {
      const g = contactsArray.at(i);
      if (!g.controls.isFromCmdb.value) continue;
      contactsArray.removeAt(i);
      removedAny = true;
    }

    if (!removedAny) return;
    ensureAtLeastOne(contactsArray, () => createContactGroup(this.fb));
    this.renderTable();
  }

  /**
   * Sort contacts so CMDB-sourced contacts appear first.
   */
  sortContactsByCmdbFirst(): void {
    const contactsArray = this.contacts();
    const current = [...contactsArray.controls];
    if (current.length <= 1) return;

    const cmdb = current.filter((g) => g.controls.isFromCmdb.value);
    const nonCmdb = current.filter((g) => !g.controls.isFromCmdb.value);
    const next = [...cmdb, ...nonCmdb];

    const unchanged = current.every((g, idx) => g === next[idx]);
    if (unchanged) return;

    for (let i = contactsArray.length - 1; i >= 0; i--) {
      contactsArray.removeAt(i);
    }
    for (const g of next) {
      contactsArray.push(g);
    }
    this.renderTable();
  }

  private findFirstEmptyContactGroup(): FormGroup<ToolContactForm> | null {
    return (
      this.contacts().controls.find((g) => {
        const n = String(g.controls.name.value ?? '').trim();
        const d = String(g.controls.contactDetail.value ?? '').trim();
        return !n && !d;
      }) ?? null
    );
  }

  private lockContactGroupFromCmdb(group: FormGroup<ToolContactForm>): void {
    if (group.controls.isFromCmdb.value) {
      if (!group.controls.type.disabled) group.controls.type.disable({ emitEvent: false });
      if (!group.controls.name.disabled) group.controls.name.disable({ emitEvent: false });
      if (!group.controls.contactDetail.disabled) group.controls.contactDetail.disable({ emitEvent: false });
      return;
    }

    group.controls.isFromCmdb.setValue(true, { emitEvent: false });
    group.controls.type.disable({ emitEvent: false });
    group.controls.name.disable({ emitEvent: false });
    group.controls.contactDetail.disable({ emitEvent: false });
  }

  private renderTable(): void {
    this.table()?.renderRows();
  }

  ngAfterViewInit(): void {
    this.renderTable();

    const arr = this.contacts();
    arr?.valueChanges?.subscribe?.(() => this.renderTable());
  }
}
